import { useIsMutating } from '@tanstack/react-query'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SimpleSelect } from '@/components/ui/select-with-more'
import { parseError } from '@/lib/errors'
import {
  POLICY_CHANGE_WRITE_KEY,
  PolicyChangeKind,
  type PolicyChangeReply,
  type PolicyChangeRequest,
  policyChangeWrites,
  usePolicyChangeMutation,
} from '@/models/policyChange'
import { ClaimStepShell } from '../Claims/ClaimStepShell'
import { AskQuestion } from './AskQuestion'
import { AskReplyFields } from './AskReplyFields'
import { ChangeCards } from './ChangeCards'
import { ChangeDates } from './ChangeDates'
import { KIND_LABELS } from './policyChangeCopy'

/** A request the broker sent back: their question, the changes to fix, and one button to send it back. */
export function PolicyChangeUpdate({ orgId, request }: { orgId: string; request: PolicyChangeRequest }) {
  const navigate = useNavigate()
  const sendUpdates = usePolicyChangeMutation(orgId, policyChangeWrites.sendUpdates)
  const [pendingDateSaves, setPendingDateSaves] = useState(0)
  const saving = useIsMutating({ mutationKey: POLICY_CHANGE_WRITE_KEY }) > 0 || pendingDateSaves > 0
  const [reply, setReply] = useState<PolicyChangeReply>({ reply: '', files: [] })
  const [signature, setSignature] = useState('')
  const ask = request.openAsk
  // Kept in state, so a kind's form stays when its last change is taken out.
  const [kinds, setKinds] = useState(() => [...new Set(request.changes.map((line) => line.kind))])
  const otherKinds = Object.values(PolicyChangeKind).filter(
    (kind) => !kinds.includes(kind) && !request.changes.some((line) => line.kind === kind)
  )
  const ready =
    request.changes.some((line) => !line.dropped) &&
    (!ask?.needsDocument || reply.files.length > 0) &&
    (request.signed || signature.trim() !== '')

  async function send() {
    try {
      await sendUpdates.mutateAsync({ requestId: request.id, askId: ask?.id, signature, ...reply })
      toast.success('Sent back to your insurance broker.')
      navigate(`/insure/policy-changes/${request.id}`)
    } catch (error) {
      toast.error(parseError(error))
    }
  }

  return (
    <>
      <Helmet>
        <title>Update your request</title>
      </Helmet>
      <ClaimStepShell step={1} title="Update your request" subtitle="" showHeader={false}>
        <div>
          <div className="mb-1 font-mono text-xs uppercase">Policy change {request.idShort}</div>
          <h1 className="text-2xl font-semibold text-neutral-900">Update your request</h1>
          <p className="mt-2 text-sm text-neutral-700">
            Change what your insurance broker asked for, then send it back.
          </p>
        </div>
        {ask && <AskQuestion ask={ask} />}
        {/* One write at a time across both editors: each returns the whole request, and the last to land wins. */}
        <fieldset disabled={saving} className="contents">
          <ChangeCards orgId={orgId} request={request} kinds={kinds} />
          <Field className="sm:max-w-sm">
            <FieldLabel htmlFor="send-updates-add-kind" optional>
              Add another change
            </FieldLabel>
            <SimpleSelect
              id="send-updates-add-kind"
              options={otherKinds.map((kind) => ({ value: kind, label: KIND_LABELS[kind] }))}
              value=""
              onValueChange={(kind) => setKinds([...kinds, kind as PolicyChangeKind])}
              placeholder="Pick what else is changing"
            />
          </Field>
          {request.changes.some((line) => !line.dropped) && (
            <section className="flex flex-col gap-3">
              <h2 className="text-lg font-semibold text-neutral-900">Effective dates</h2>
              <ChangeDates orgId={orgId} request={request} setPendingSaves={setPendingDateSaves} />
            </section>
          )}
        </fieldset>
        <Card>
          <CardHeader>
            <CardTitle>Anything else for your insurance broker?</CardTitle>
          </CardHeader>
          <CardContent>
            <AskReplyFields
              id="send-updates"
              value={reply}
              onChange={setReply}
              needsDocument={ask?.needsDocument ?? false}
              replyLabel="Note"
              replyOptional
            />
          </CardContent>
        </Card>
        {!request.signed && (
          <Field className="sm:max-w-sm">
            <FieldLabel htmlFor="send-updates-signature">Digital signature (full legal name)</FieldLabel>
            <Input
              id="send-updates-signature"
              placeholder="Type your full legal name"
              value={signature}
              onChange={(event) => setSignature(event.target.value)}
            />
          </Field>
        )}
        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to={`/insure/policy-changes/${request.id}`}>← Back</Link>
          </Button>
          <Button
            disabled={!ready || saving}
            onClick={() => void send()}
            loading={sendUpdates.isPending ? 'Sending…' : undefined}
          >
            Send updates
          </Button>
        </div>
      </ClaimStepShell>
    </>
  )
}
